import React, { useCallback, useEffect, useState } from 'react';
import {
  Alert, Box, Button, Chip, Collapse, IconButton, LinearProgress, Table, TableBody, TableCell, TableContainer, TableHead,
  TablePagination, TableRow, Tooltip, Typography
} from '@mui/material';
import { KeyboardArrowDown as ExpandIcon, KeyboardArrowUp as CollapseIcon } from '@mui/icons-material';
import { apiError } from '../../Documents/format';
import { RUN_KIND_LABELS, RUN_STATUS_COLORS, RUN_STATUS_LABELS, SCOPE_LABELS, TRIGGER_LABELS } from '../constants';
import { isCancelled, timesheetApi } from '../timesheetApi';
import { RunsPage } from '../types';
import { fmtMoment, runTotals } from '../utils';
import RunItems from './RunItems';

interface Props {
  /** Меняется, когда запуск завершился: история перечитывается */
  refreshKey: string;
}

const BORDER = '#e2e8f0';
const HEAD_SX = { fontWeight: 700, fontSize: 12, color: '#475569', bgcolor: '#f8fafc', borderBottom: `1px solid ${BORDER}`, whiteSpace: 'nowrap' } as const;
const COLUMNS = 8;

/** История запусков создания табелей (по 20 на страницу): вид и способ запуска, причина, итог; строка раскрывается до результата по аптекам */
const RunHistory: React.FC<Props> = ({ refreshKey }) => {
  const [page, setPage] = useState(1);
  const [data, setData] = useState<RunsPage | null>(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [reloadToken, setReloadToken] = useState(0);
  const [expanded, setExpanded] = useState<number | null>(null);

  useEffect(() => {
    const controller = new AbortController();
    setLoading(true);
    timesheetApi.runs(page, controller.signal)
      .then(res => { setData(res); setError(null); })
      .catch(err => { if (!isCancelled(err)) setError(apiError(err, 'Не удалось загрузить историю запусков')); })
      .finally(() => { if (!controller.signal.aborted) setLoading(false); });
    return () => controller.abort();
  }, [page, refreshKey, reloadToken]);

  const retry = useCallback(() => setReloadToken(t => t + 1), []);

  if (error && !data) {
    return <Alert severity="error" action={<Button color="inherit" size="small" onClick={retry}>Повторить</Button>}>{error}</Alert>;
  }

  const runs = data ? data.runs : [];
  const pagination = data ? data.pagination : { total: 0, page: 1, limit: 20 };

  return (
    <Box sx={{ border: `1px solid ${BORDER}`, borderRadius: 2, overflow: 'hidden' }}>
      <Box sx={{ height: 3 }}>{loading && <LinearProgress sx={{ height: 3 }} />}</Box>
      <TableContainer sx={{ maxHeight: 360 }}>
        <Table size="small" stickyHeader sx={{ minWidth: 760 }}>
          <TableHead>
            <TableRow>
              <TableCell sx={{ ...HEAD_SX, width: 36, px: 0.5 }} />
              <TableCell sx={HEAD_SX}>№</TableCell>
              <TableCell sx={HEAD_SX}>Запуск</TableCell>
              <TableCell sx={HEAD_SX}>Кто и когда</TableCell>
              <TableCell sx={HEAD_SX}>Зачем</TableCell>
              <TableCell sx={HEAD_SX}>Зона</TableCell>
              <TableCell sx={HEAD_SX}>Итог</TableCell>
              <TableCell sx={HEAD_SX}>Статус</TableCell>
            </TableRow>
          </TableHead>
          <TableBody>
            {runs.map(run => {
              const open = expanded === run.id;
              return (
                <React.Fragment key={run.id}>
                  <TableRow hover sx={{ '& td': { borderBottom: open ? 'none' : '1px solid #f1f5f9', py: 0.75, fontSize: 13, verticalAlign: 'top' } }}>
                    <TableCell sx={{ px: 0.5 }}>
                      <Tooltip title={open ? 'Скрыть результат по аптекам' : 'Результат по аптекам'}>
                        <IconButton size="small" onClick={() => setExpanded(open ? null : run.id)} aria-label="Результат по аптекам">
                          {open ? <CollapseIcon fontSize="small" /> : <ExpandIcon fontSize="small" />}
                        </IconButton>
                      </Tooltip>
                    </TableCell>
                    <TableCell sx={{ fontWeight: 700 }}>{run.id}</TableCell>
                    <TableCell>
                      <Typography variant="body2" sx={{ fontSize: 13 }}>{RUN_KIND_LABELS[run.kind] || run.kind}, {run.year}</Typography>
                      <Typography variant="caption" sx={{ color: '#94a3b8' }}>{TRIGGER_LABELS[run.triggerType] || run.triggerType}</Typography>
                    </TableCell>
                    <TableCell sx={{ color: '#475569' }}>
                      <Typography variant="body2" sx={{ fontSize: 13 }}>{run.startedBy || '-'}</Typography>
                      <Typography variant="caption" sx={{ color: '#94a3b8', whiteSpace: 'nowrap' }}>{fmtMoment(run.startedAt)}</Typography>
                    </TableCell>
                    <TableCell sx={{ color: '#475569', minWidth: 140, maxWidth: 220 }}>
                      {run.reason ? (
                        <Tooltip title={run.reason} placement="top-start">
                          <Typography
                            variant="body2"
                            sx={{ fontSize: 13, overflow: 'hidden', display: '-webkit-box', WebkitLineClamp: 2, WebkitBoxOrient: 'vertical' }}
                          >
                            {run.reason}
                          </Typography>
                        </Tooltip>
                      ) : '-'}
                    </TableCell>
                    <TableCell sx={{ color: '#475569' }}>{SCOPE_LABELS[run.scope] || run.scope}</TableCell>
                    <TableCell sx={{ color: '#475569' }}>{runTotals(run)}</TableCell>
                    <TableCell><Chip size="small" variant="outlined" color={RUN_STATUS_COLORS[run.status]} label={RUN_STATUS_LABELS[run.status]} /></TableCell>
                  </TableRow>
                  <TableRow>
                    <TableCell colSpan={COLUMNS} sx={{ p: 0, borderBottom: open ? '1px solid #f1f5f9' : 'none' }}>
                      <Collapse in={open} timeout="auto" unmountOnExit>
                        <RunItems runId={run.id} />
                      </Collapse>
                    </TableCell>
                  </TableRow>
                </React.Fragment>
              );
            })}
            {!loading && runs.length === 0 && (
              <TableRow>
                <TableCell colSpan={COLUMNS} align="center" sx={{ py: 4 }}>
                  <Typography variant="body2" color="text.secondary">Запусков ещё не было</Typography>
                </TableCell>
              </TableRow>
            )}
          </TableBody>
        </Table>
      </TableContainer>
      {pagination.total > pagination.limit && (
        <TablePagination
          component="div" count={pagination.total} page={Math.max(0, pagination.page - 1)} rowsPerPage={pagination.limit}
          rowsPerPageOptions={[]} onPageChange={(_, p) => { setExpanded(null); setPage(p + 1); }}
          labelDisplayedRows={({ from, to, count }) => `${from}–${to} из ${count}`}
          sx={{ borderTop: `1px solid ${BORDER}` }}
        />
      )}
    </Box>
  );
};

export default React.memo(RunHistory);
